import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Maximize2, Weight, X } from "lucide-react";
import { Package } from "@/types";
import { toast } from "sonner";

interface EditPackageModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  packageData: Package | null;
  onSave: (id: number, changes: Partial<Package>) => Promise<void>;
}

const EditPackageModal = ({
  open,
  onOpenChange,
  packageData,
  onSave,
}: EditPackageModalProps) => {
  const [guestName, setGuestName] = useState("");
  const [tracking, setTracking] = useState("");
  const [roomNumber, setRoomNumber] = useState("");
  const [observations, setObservations] = useState({ oversized: false, overweight: false });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setGuestName(packageData?.guest_name || "");
    setTracking(packageData?.tracking || "");
    setRoomNumber(packageData?.room_number || "");
    setObservations(packageData?.observations || { oversized: false, overweight: false });
  }, [packageData, open]);

  const handleObservationToggle = (key: 'oversized' | 'overweight') => {
    setObservations(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    if (!packageData) return;
    if (!guestName.trim() || !tracking.trim()) {
      toast.error("Please fill all required fields");
      return;
    }
    setIsSaving(true);
    await onSave(packageData.id, {
      guest_name: guestName,
      tracking,
      room_number: roomNumber,
      observations,
    });
    setIsSaving(false);
    onOpenChange(false);
  };

  if (!packageData) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Package</DialogTitle>
          <DialogClose className="absolute right-4 top-4">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <label className="text-sm font-medium">Guest Name</label>
            <Input
              value={guestName}
              onChange={e => setGuestName(e.target.value)}
              placeholder="Guest Name"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Tracking Number</label>
            <Input
              value={tracking}
              onChange={e => setTracking(e.target.value)}
              placeholder="Tracking Number"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Unit Number</label>
            <Input
              value={roomNumber}
              onChange={e => setRoomNumber(e.target.value)}
              placeholder="Unit Number"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Observations</label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={observations.oversized ? "default" : "outline"}
                onClick={() => handleObservationToggle("oversized")}
                className={observations.oversized
                  ? "bg-primary text-primary-foreground border-transparent hover:bg-primary/90"
                  : "bg-white border-gray-300 hover:bg-primary/10 text-primary"}
              >
                <Maximize2 size={18} className={observations.oversized ? "text-primary-foreground" : "text-primary"} />
                <span className="ml-2">Oversized</span>
              </Button>
              <Button
                type="button"
                variant={observations.overweight ? "default" : "outline"}
                onClick={() => handleObservationToggle("overweight")}
                className={observations.overweight
                  ? "bg-primary text-primary-foreground border-transparent hover:bg-primary/90"
                  : "bg-white border-gray-300 hover:bg-primary/10 text-primary"}
              >
                <Weight size={18} className={observations.overweight ? "text-primary-foreground" : "text-primary"} />
                <span className="ml-2">Overweight</span>
              </Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditPackageModal; 